import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "asya_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso || "—";
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // {total, entries}

  const loadData = useCallback(async (tk) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error(err?.response?.data?.detail || "Session expired. Please sign in again.");
      } else {
        toast.error("Could not load signups.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      toast.success("Signed in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  const downloadCSV = () => {
    if (!data?.entries?.length) return;
    const cols = ["name", "email", "referrals", "referred_by", "referral_code", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = data.entries.map((e) => cols.map((c) => esc(e[c])).join(","));
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asya-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm">
          <p className="overline mb-4">ASYA · Admin</p>
          <h1 className="font-serif font-light text-4xl mb-8 tracking-[-0.02em]">Sign in</h1>
          <div className="space-y-6">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email</label>
              <input
                data-testid="admin-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-primary outline-none py-3 text-lg transition-colors"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Password</label>
              <input
                data-testid="admin-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-primary outline-none py-3 text-lg transition-colors"
              />
            </div>
          </div>
          <button
            data-testid="admin-login-button"
            type="submit" disabled={loading}
            className="mt-10 w-full rounded-full bg-accent px-8 py-4 text-accent-foreground text-sm font-medium hover:bg-white transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  // ---- Dashboard ----
  const entries = data?.entries || [];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 glass border-b border-white/10">
        <div className="mx-auto max-w-[1200px] px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl">ASYA</span>
            <span className="overline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => loadData(token)} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs hover:border-white/50 transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-export-csv" onClick={downloadCSV} className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-accent-foreground text-xs font-medium hover:bg-white transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs hover:border-white/50 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <Users className="w-5 h-5 text-primary" />
          <h1 className="font-serif font-light text-3xl tracking-[-0.02em]">
            Waitlist <span className="text-muted-foreground">· {entries.length.toLocaleString()} signups</span>
          </h1>
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-waitlist-table">
              <thead>
                <tr className="text-left border-b border-white/10 bg-card/50">
                  {["#", "Name", "Email", "Referrals", "Referred by", "Joined"].map((h) => (
                    <th key={h} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-5 py-4 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No signups yet.</td></tr>
                )}
                {entries.map((e, i) => (
                  <tr key={e.referral_code || e.email} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                    <td className="px-5 py-4 text-muted-foreground font-mono text-xs">{i + 1}</td>
                    <td className="px-5 py-4 whitespace-nowrap">{e.name || "—"}</td>
                    <td className="px-5 py-4 whitespace-nowrap">{e.email}</td>
                    <td className="px-5 py-4">
                      <span className={e.referrals > 0 ? "text-primary" : "text-muted-foreground"}>{e.referrals || 0}</span>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs text-muted-foreground">{e.referred_by || "—"}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-muted-foreground">{fmtDate(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
